// AppModal.jsx
// The one canonical popup for the whole app. Wraps MUI <Dialog> in a brand-matched,
// AppModal-scoped theme (see appModalTheme.js) and locks down the chrome so every
// popup looks and feels the same: consistent header, close button, footer buttons,
// size scale, and loading state (brand LoadingOverlay + button spinner).
//
// Functionality of the popups doesn't change — only their shell. Existing reactstrap
// and MUI dialogs migrate by swapping their outer <Modal>/<Dialog> for <AppModal>
// and dropping their hand-rolled header/footer/spinner.
//
// Minimal usage:
//   <AppModal open={open} onClose={close} title="Edit customer"
//             size="md" loading={saving} onSubmit={save} submitLabel="Save">
//     {/* form fields */}
//   </AppModal>
//
// Migration aliases (so old call-sites drop in): `isOpen`/`show` → open,
// `toggle` → onClose. Prefer `open`/`onClose` in new code.

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Stack,
  Typography,
  CircularProgress,
  ThemeProvider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { buildAppModalTheme } from "./appModalTheme";
import "./appModal.scss";

/* Our size scale → MUI maxWidth. Keeps every popup on one width system. */
const SIZE_MAP = { xs: "xs", sm: "sm", md: "md", lg: "lg", xl: "xl" };

export default function AppModal({
  // open / close (with migration aliases)
  open,
  isOpen,
  show,
  onClose,
  toggle,

  // header
  title,
  subtitle,
  icon,
  // Functional controls that belong in the header bar (not chrome) — e.g. a
  // Back button, an "Add"/"New" action, a Download icon, a toggle. Rendered on
  // the right of the title, before the close button. On a gradient header they
  // inherit white chrome for contrast (style via .app-modal__header-actions).
  headerActions,
  hideClose = false,
  // Opt-in gradient header (InfoCard look): "indigo" | "blue" | "sky" |
  // "violet" | "emerald" | "amber" | "rose" | "teal" | "slate".
  headerAccent,

  // sizing
  size = "sm",
  fullWidth = true,
  scroll = "paper",

  // body
  children,
  dividers = true,
  contentProps = {},

  // loading
  loading = false,
  loadingText = "Please wait…",

  // footer — default two-button footer, or pass `footer` to fully override
  footer,
  // Task 24615 - an optional third button, sitting left of Cancel/Save, for
  // the "and do it again" variant of the primary action ("Save & add
  // another"). A node rather than a label+handler pair so the caller keeps
  // full control; `footer` is still the escape hatch for anything richer.
  secondaryAction,
  onSubmit,
  submitLabel = "Save",
  submitColor = "primary",
  submitDisabled = false,
  cancelLabel = "Cancel",
  hideCancel = false,
  hideFooter = false,

  // escape hatches
  dialogProps = {},
}) {
  const isShown = open ?? isOpen ?? show ?? false;
  const handleClose = onClose ?? toggle ?? (() => {});

  // Rebuild the brand theme each time the modal opens so it captures the live
  // Bootstrap tokens (incl. light/dark mode) at that moment. `isShown` is the
  // intentional trigger even though it isn't read inside the factory.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const theme = useMemo(() => buildAppModalTheme(), [isShown]);

  // Never let a backdrop/escape click close mid-save.
  const guardedClose = (_e, reason) => {
    if (loading && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
    handleClose();
  };

  const hasDefaultFooter =
    !hideFooter && !footer && (typeof onSubmit === "function" || !hideCancel);

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={isShown}
        onClose={guardedClose}
        maxWidth={SIZE_MAP[size] || "sm"}
        fullWidth={fullWidth}
        scroll={scroll}
        className="app-modal"
        // Don't trap focus inside the Dialog: popups that portal their overlay to
        // document.body (react-flatpickr calendars, reactstrap dropdowns, some MUI
        // menus) live outside this subtree. With enforceFocus on (MUI default), the
        // first click into such a popup gets its focus yanked straight back into the
        // Dialog, which instantly closes the popup — e.g. the date/time picker in
        // Create New Job appeared dead after the modal-unification migration.
        disableEnforceFocus
        {...dialogProps}
      >
        {(title || headerActions || !hideClose) && (
          <DialogTitle
            component="div"
            className={`app-modal__title${
              headerAccent
                ? ` app-modal__title--gradient app-modal__title--${headerAccent}`
                : ""
            }`}
          >
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              {/* Task 24772 - a STRING is treated as an icon class, not as
                  text. InfoCard, Chip and most of the shared components take
                  `icon` as a remix/font-awesome class, so `icon="ri-price-
                  tag-3-line"` is the natural thing to write here - and it
                  used to render those words, literally, next to the title.
                  Accepting both shapes fixes every call site at once,
                  including the ones nobody has looked at yet. */}
              {icon && (
                <Box className="app-modal__icon">
                  {typeof icon === "string" ? <i className={icon} /> : icon}
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {title && (
                  <Typography variant="h6" className="app-modal__title-text" noWrap>
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {headerActions && (
                <Box className="app-modal__header-actions">{headerActions}</Box>
              )}
              {!hideClose && (
                <IconButton
                  aria-label="Close"
                  onClick={handleClose}
                  disabled={loading}
                  size="small"
                  edge="end"
                  className="app-modal__close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </DialogTitle>
        )}

        <DialogContent
          dividers={dividers}
          className="app-modal__content"
          sx={{ position: "relative" }}
          {...contentProps}
        >
          {children}
          <LoadingOverlay show={loading} title={loadingText} minHeight={0} />
        </DialogContent>

        {footer
          ? !hideFooter && (
              <DialogActions className="app-modal__footer">{footer}</DialogActions>
            )
          : hasDefaultFooter && (
              <DialogActions className="app-modal__footer">
                {secondaryAction}
                {!hideCancel && (
                  <Button color="inherit" onClick={handleClose} disabled={loading}>
                    {cancelLabel}
                  </Button>
                )}
                {typeof onSubmit === "function" && (
                  <Button
                    variant="contained"
                    color={submitColor}
                    onClick={onSubmit}
                    disabled={loading || submitDisabled}
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    {submitLabel}
                  </Button>
                )}
              </DialogActions>
            )}
      </Dialog>
    </ThemeProvider>
  );
}

AppModal.propTypes = {
  open: PropTypes.bool,
  isOpen: PropTypes.bool,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  toggle: PropTypes.func,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  // Task 24772 - a node, or an icon class string ("ri-...", "fa-solid ...").
  icon: PropTypes.node,
  headerActions: PropTypes.node,
  hideClose: PropTypes.bool,
  headerAccent: PropTypes.oneOf([
    "indigo",
    "blue",
    "sky",
    "violet",
    "emerald",
    "amber",
    "rose",
    "teal",
    "slate",
  ]),
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  fullWidth: PropTypes.bool,
  scroll: PropTypes.oneOf(["paper", "body"]),
  children: PropTypes.node,
  dividers: PropTypes.bool,
  contentProps: PropTypes.object,
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  footer: PropTypes.node,
  secondaryAction: PropTypes.node,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.node,
  submitColor: PropTypes.string,
  submitDisabled: PropTypes.bool,
  cancelLabel: PropTypes.node,
  hideCancel: PropTypes.bool,
  hideFooter: PropTypes.bool,
  dialogProps: PropTypes.object,
};
